import React from 'react';
import { WEEKDAY_NAMES, WEEKDAY_SHORT_NAMES } from '../../../types/working-hours';
import TimeRangeInput from './TimeRangeInput';
import { Plus } from 'lucide-react';

interface WorkingHourInput {
  id?: string;
  weekday: number;
  start_min: number;
  end_min: number;
  _destroy?: boolean;
}

interface DayScheduleRowProps {
  weekday: number;
  hours: WorkingHourInput[];
  onChange: (hours: WorkingHourInput[]) => void;
}

const DayScheduleRow: React.FC<DayScheduleRowProps> = ({
  weekday,
  hours,
  onChange,
}) => {
  const activeHours = hours.filter(h => !h._destroy);
  const isWorking = activeHours.length > 0;

  const handleToggleDay = () => {
    if (isWorking) {
      onChange(hours.map(h => ({ ...h, _destroy: true })));
    } else {
      onChange([
        ...hours,
        { weekday, start_min: 540, end_min: 1020 },
      ]);
    }
  };

  const handleAddTimeRange = () => {
    const lastEnd = activeHours.length > 0
      ? Math.max(...activeHours.map(h => h.end_min))
      : 540;

    const newStart = Math.min(lastEnd + 60, 1380);
    const newEnd = Math.min(newStart + 240, 1439);

    onChange([...hours, { weekday, start_min: newStart, end_min: newEnd }]);
  };

  const handleUpdateTimeRange = (index: number, start_min: number, end_min: number) => {
    const newHours = [...hours];
    const activeIndex = hours.findIndex((h, i) =>
      !h._destroy && hours.slice(0, i + 1).filter(x => !x._destroy).length === index + 1
    );
    if (activeIndex !== -1) {
      newHours[activeIndex] = { ...newHours[activeIndex], start_min, end_min };
      onChange(newHours);
    }
  };

  const handleRemoveTimeRange = (index: number) => {
    const newHours = [...hours];
    let activeCount = 0;
    for (let i = 0; i < newHours.length; i++) {
      if (!newHours[i]._destroy) {
        if (activeCount === index) {
          if (newHours[i].id) {
            newHours[i] = { ...newHours[i], _destroy: true };
          } else {
            newHours.splice(i, 1);
          }
          break;
        }
        activeCount++;
      }
    }
    onChange(newHours);
  };

  return (
    <div className={`
      flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-3 sm:py-3.5
      transition-colors
      ${isWorking ? 'bg-white' : 'bg-gray-50/60'}
    `}>
      {/* Day toggle + name */}
      <div className="flex items-center gap-2.5 sm:gap-3 w-[88px] sm:w-[130px] flex-shrink-0">
        <button
          onClick={handleToggleDay}
          className={`
            relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full
            transition-colors duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:ring-offset-1
            ${isWorking ? 'bg-purple-600' : 'bg-gray-300'}
          `}
        >
          <span
            className={`
              pointer-events-none inline-block h-4 w-4 transform rounded-full
              bg-white shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5 ml-0.5
              ${isWorking ? 'translate-x-[14px]' : 'translate-x-0'}
            `}
          />
        </button>
        <span className={`
          text-sm font-medium tracking-tight
          ${isWorking ? 'text-gray-900' : 'text-gray-400'}
        `}>
          <span className="sm:hidden">{WEEKDAY_SHORT_NAMES[weekday]}</span>
          <span className="hidden sm:inline">{WEEKDAY_NAMES[weekday]}</span>
        </span>
      </div>

      {/* Time ranges + add button — all on one line */}
      <div className="flex-1 min-w-0">
        {isWorking ? (
          <div className="flex flex-wrap items-center gap-2">
            {activeHours.map((hour, index) => (
              <TimeRangeInput
                key={hour.id || `new-${index}`}
                startMin={hour.start_min}
                endMin={hour.end_min}
                onChange={(start, end) => handleUpdateTimeRange(index, start, end)}
                onRemove={activeHours.length > 1 ? () => handleRemoveTimeRange(index) : undefined}
              />
            ))}
            <button
              onClick={handleAddTimeRange}
              className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md px-1.5 py-1 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add</span>
            </button>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Closed</span>
        )}
      </div>
    </div>
  );
};

export default DayScheduleRow;
